import { Capacitor } from "@capacitor/core";
import styled from "@emotion/styled";
import assertNever from "assert-never";
import Linkify from "linkify-react";
import React from "react";
import { API } from "../API";
import { openExternalUrl } from "../external_link";
import { ChatPayload } from "../messages";
import { ChatMessageSender } from "./ChatRoomScreen";
import SignedS3Photo from "./Generic/SignedPhoto";
import Spinner from "./Generic/Spinner";

interface Props {
  payload: ChatPayload;
  seenStatus: boolean;
  sender: ChatMessageSender;
  footer: JSX.Element;
  getSignedPhotoUrl: API["getSignedPhotoUrl"];
}

function ChatMessagePayload(props: Props): JSX.Element {
  const bubbleExtraCssClass = (() => {
    switch (props.sender) {
      case ChatMessageSender.Received:
        return "ChatRoomScreen__bubble_received";
      case ChatMessageSender.Sending:
        return "ChatRoomScreen__bubble_sending";
      case ChatMessageSender.Sent:
        return "ChatRoomScreen__bubble_sent";
      default:
        return assertNever(props.sender);
    }
  })();

  switch (props.payload.type) {
    case "Message":
      return (
        <div className={"ChatRoomScreen__bubble " + bubbleExtraCssClass}>
          {(props.payload.images ?? []).length > 0 ? (
            <ChatMessagePayload.Images>
              {(props.payload.images ?? []).map((image) => (
                <ChatMessagePayload.ImageWrapper key={image}>
                  {props.sender === ChatMessageSender.Sending ? <Spinner size={48} /> : null}
                  <ChatMessagePayload.Image
                    url={image}
                    getSignedPhotoUrl={props.getSignedPhotoUrl}
                    onClick={(signed) => window.open(signed)}
                  />
                </ChatMessagePayload.ImageWrapper>
              ))}
            </ChatMessagePayload.Images>
          ) : null}
          <Linkify
            tagName="span"
            options={{
              nl2br: true,
              target: "_system",
              attributes: {
                onClick: (e: any) => {
                  if (Capacitor.getPlatform() === "ios") {
                    openExternalUrl(e.target.href);
                  }
                },
              },
            }}
          >
            {props.payload.message}
          </Linkify>
          <div className="ChatRoomScreen__bubble__timestamp">
            {props.footer}
            <span className="ChatRoomScreen_seen_div">
              {props.seenStatus ? (
                <img className="ChatRoomScreen_seen_img" src="assets/seen_icon.png" />
              ) : null}
            </span>
          </div>
        </div>
      );
    case "Image":
      return <div>TODO</div>;
    default:
      return assertNever(props.payload);
  }
}

ChatMessagePayload.Images = styled.div`
  margin-bottom: 8px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

ChatMessagePayload.ImageWrapper = styled.div`
  position: relative;

  > ${Spinner.Styled} {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    margin: auto;
    background-color: rgba(0, 0, 0, 0.5);
    border-radius: 50%;
    padding: 8px;
  }
`;

ChatMessagePayload.Image = styled(SignedS3Photo)`
  height: 200px;
  min-width: min(250px, 70vw);

  .AsyncImage {
    border-radius: 8px;
  }
`;

export default ChatMessagePayload;
